import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths, parseISO, addWeeks, subWeeks, startOfDay, endOfDay } from 'date-fns';
import { hu } from 'date-fns/locale';
import { CalendarEvent, Event, EventType } from '../types/event';

export const HUNGARIAN_WEEKDAYS = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];
export const HUNGARIAN_WEEKDAYS_SHORT = ['H', 'K', 'Sz', 'Cs', 'P', 'Sz', 'V'];

export interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
}

export interface WeekView {
  days: CalendarDay[];
  weekStart: Date;
  weekEnd: Date;
}

export const getCalendarDays = (currentDate: Date, events: Event[]): CalendarDay[] => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  return calendarDays.map(date => ({
    date,
    isCurrentMonth: isSameMonth(date, currentDate),
    isToday: isToday(date),
    events: getEventsForDay(date, events),
  }));
};

export const getWeekDays = (currentDate: Date, events: Event[]): WeekView => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });

  const weekDays = eachDayOfInterval({
    start: weekStart,
    end: weekEnd,
  });

  const days = weekDays.map(date => ({
    date,
    isCurrentMonth: true,
    isToday: isToday(date),
    events: getEventsForDay(date, events),
  }));

  return {
    days,
    weekStart,
    weekEnd,
  };
};

export const getEventsForDay = (date: Date, events: Event[]): CalendarEvent[] => {
  return events
    .filter(event => {
      const eventStart = parseISO(event.startTime);
      const eventEnd = parseISO(event.endTime);
      const dayStart = startOfDay(date);
      const dayEnd = endOfDay(date);

      // Event overlaps with the day
      return eventStart <= dayEnd && eventEnd >= dayStart;
    })
    .map(event => ({
      ...event,
      color: getEventColor(event.type),
      isAllDay: isAllDayEvent(event),
      duration: getEventDuration(event),
    }))
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
};

export const getEventColor = (eventType: EventType): string => {
  const colors = {
    [EventType.Training]: 'bg-blue-100 text-blue-800 border-blue-200',
    [EventType.Match]: 'bg-red-100 text-red-800 border-red-200',
    [EventType.Meeting]: 'bg-purple-100 text-purple-800 border-purple-200',
    [EventType.Other]: 'bg-gray-100 text-gray-800 border-gray-200',
  };
  return colors[eventType] || colors[EventType.Other];
};

export const getEventTypeLabel = (eventType: EventType): string => {
  const labels = {
    [EventType.Training]: 'Edzés',
    [EventType.Match]: 'Mérkőzés',
    [EventType.Meeting]: 'Megbeszélés',
    [EventType.Other]: 'Egyéb',
  };
  return labels[eventType] || labels[EventType.Other];
};

export const isAllDayEvent = (event: Event): boolean => {
  const start = parseISO(event.startTime);
  const end = parseISO(event.endTime);
  const duration = end.getTime() - start.getTime();
  const hours = duration / (1000 * 60 * 60);

  // Consider it all-day if it's longer than 12 hours or starts at midnight
  return hours >= 12 || (start.getHours() === 0 && start.getMinutes() === 0);
};

export const getEventDuration = (event: Event): number => {
  const start = parseISO(event.startTime);
  const end = parseISO(event.endTime);
  return end.getTime() - start.getTime();
};

export const formatEventTime = (event: Event): string => {
  const start = parseISO(event.startTime);
  const end = parseISO(event.endTime);

  if (isAllDayEvent(event)) {
    return 'Egész nap';
  }

  const startTime = format(start, 'HH:mm', { locale: hu });
  const endTime = format(end, 'HH:mm', { locale: hu });

  if (isSameDay(start, end)) {
    return `${startTime} - ${endTime}`;
  } else {
    const startDate = format(start, 'MMM d, HH:mm', { locale: hu });
    const endDate = format(end, 'MMM d, HH:mm', { locale: hu });
    return `${startDate} - ${endDate}`;
  }
};

export const getTimeSlots = (): string[] => {
  const slots = [];
  for (let hour = 6; hour <= 22; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
  }
  return slots;
};

export const getEventPosition = (event: CalendarEvent, dayStart: Date): { top: number; height: number } => {
  const eventStart = parseISO(event.startTime);
  const eventEnd = parseISO(event.endTime);

  const dayStartHour = 6; // 6 AM
  const pixelsPerHour = 60; // 60px per hour

  const eventStartMinutes = (eventStart.getHours() - dayStartHour) * 60 + eventStart.getMinutes();
  const eventDurationMinutes = (eventEnd.getTime() - eventStart.getTime()) / (1000 * 60);

  const top = Math.max(0, (eventStartMinutes / 60) * pixelsPerHour);
  const height = Math.max(20, (eventDurationMinutes / 60) * pixelsPerHour);

  return { top, height };
};

export const navigateMonth = (currentDate: Date, direction: 'prev' | 'next'): Date => {
  return direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1);
};

export const navigateWeek = (currentDate: Date, direction: 'prev' | 'next'): Date => {
  return direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1);
};